import { apiClient } from '@/lib/api';
import { ENDPOINTS } from '@/lib/api/endpoints';
import {
  PricingModel,
  CreatePricingModelData,
  CreateFromTemplateData,
  UpdatePricingModelData,
  CreateVariableData,
  UpdateVariableData,
  Variable,
  LookupTable,
  CreateLookupTableData,
  UpdateLookupTableData,
} from '../types/model.types';

/**
 * Servicio para modelos de pricing
 */
class ModelService {
  // ============ Pricing Models ============
  async getModels(): Promise<PricingModel[]> {
    return apiClient.get<PricingModel[]>(ENDPOINTS.PRICING_MODELS);
  }

  async getModel(id: string): Promise<PricingModel> {
    return apiClient.get<PricingModel>(`${ENDPOINTS.PRICING_MODELS}/${id}`);
  }

  async createModel(data: CreatePricingModelData): Promise<PricingModel> {
    return apiClient.post<PricingModel>(ENDPOINTS.PRICING_MODELS, data);
  }

  async createFromTemplate(data: CreateFromTemplateData): Promise<PricingModel> {
    return apiClient.post<PricingModel>(`${ENDPOINTS.PRICING_MODELS}/from-template`, data);
  }

  async updateModel(id: string, data: UpdatePricingModelData): Promise<PricingModel> {
    return apiClient.patch<PricingModel>(`${ENDPOINTS.PRICING_MODELS}/${id}`, data);
  }

  async deleteModel(id: string): Promise<void> {
    return apiClient.delete<void>(`${ENDPOINTS.PRICING_MODELS}/${id}`);
  }

  async cloneModel(id: string): Promise<PricingModel> {
    return apiClient.post<PricingModel>(`${ENDPOINTS.PRICING_MODELS}/${id}/clone`, {});
  }

  // ============ Variables ============
  async getVariables(modelId: string): Promise<Variable[]> {
    return apiClient.get<Variable[]>(`${ENDPOINTS.PRICING_MODELS}/${modelId}/variables`);
  }

  async createVariable(modelId: string, data: CreateVariableData): Promise<Variable> {
    return apiClient.post<Variable>(`${ENDPOINTS.PRICING_MODELS}/${modelId}/variables`, data);
  }

  async updateVariable(
    modelId: string,
    variableId: string,
    data: UpdateVariableData
  ): Promise<Variable> {
    return apiClient.patch<Variable>(
      `${ENDPOINTS.PRICING_MODELS}/${modelId}/variables/${variableId}`,
      data
    );
  }

  async deleteVariable(modelId: string, variableId: string): Promise<void> {
    return apiClient.delete<void>(`${ENDPOINTS.PRICING_MODELS}/${modelId}/variables/${variableId}`);
  }

  async reorderVariables(modelId: string, variableIds: string[]): Promise<void> {
    return apiClient.post<void>(`${ENDPOINTS.PRICING_MODELS}/${modelId}/variables/reorder`, {
      variableIds,
    });
  }

  // ============ Lookup Tables ============
  async getLookupTables(modelId: string): Promise<LookupTable[]> {
    return apiClient.get<LookupTable[]>(`${ENDPOINTS.PRICING_MODELS}/${modelId}/lookup-tables`);
  }

  async createLookupTable(modelId: string, data: CreateLookupTableData): Promise<LookupTable> {
    return apiClient.post<LookupTable>(
      `${ENDPOINTS.PRICING_MODELS}/${modelId}/lookup-tables`,
      data
    );
  }

  async updateLookupTable(
    modelId: string,
    tableId: string,
    data: UpdateLookupTableData
  ): Promise<LookupTable> {
    return apiClient.patch<LookupTable>(
      `${ENDPOINTS.PRICING_MODELS}/${modelId}/lookup-tables/${tableId}`,
      data
    );
  }

  async deleteLookupTable(modelId: string, tableId: string): Promise<void> {
    return apiClient.delete<void>(
      `${ENDPOINTS.PRICING_MODELS}/${modelId}/lookup-tables/${tableId}`
    );
  }
}

export const modelService = new ModelService();
export default modelService;
